import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Flame, Utensils, Trophy, Target, Heart, Star, Zap, Calendar, Crown, Medal, ClipboardList, Sparkles, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatWeight, getWeightUnit } from '../utils/weightUnit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  flame: Flame, utensils: Utensils, trophy: Trophy, target: Target,
  heart: Heart, star: Star, zap: Zap, calendar: Calendar,
  crown: Crown, medal: Medal, clipboard: ClipboardList, sparkles: Sparkles,
};

const StreaksWidget = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();
  const system = user?.measurement_system || user?.profile?.measurement_system || 'metric';
  const unit = getWeightUnit(system);

  useEffect(() => { fetchAchievements(); }, []);

  const fetchAchievements = async () => {
    try {
      const res = await axios.get(`${API}/exercise/achievements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading || !data) {
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm animate-pulse" data-testid="streaks-widget-loading">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
        <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  const unlocked = data.achievements.filter(a => a.unlocked);
  const locked = data.achievements.filter(a => !a.unlocked);
  const displayed = showAll ? data.achievements : [...unlocked.slice(0, 6), ...locked.slice(0, Math.max(0, 6 - unlocked.length))];

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="streaks-widget">
      {/* Streaks Row */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/10 p-3 rounded-xl text-center" data-testid="workout-streak">
          <Flame size={24} className="mx-auto text-[#FF6600] mb-1" />
          <p className="text-2xl font-extrabold text-[#FF6600]">{data.workout_streak}</p>
          <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">Workout Streak</p>
        </div>
        <div className="flex-1 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/10 p-3 rounded-xl text-center" data-testid="meal-streak">
          <Utensils size={24} className="mx-auto text-green-600 mb-1" />
          <p className="text-2xl font-extrabold text-green-600">{data.meal_streak}</p>
          <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">Meal Streak</p>
        </div>
        <div className="flex-1 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-900/10 p-3 rounded-xl text-center" data-testid="achievement-count">
          <Trophy size={24} className="mx-auto text-purple-600 mb-1" />
          <p className="text-2xl font-extrabold text-purple-600">{data.unlocked_count}</p>
          <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">of {data.total_count} Badges</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex gap-2 mb-4 text-center">
        <div className="flex-1 bg-gray-50 dark:bg-gray-800 p-2 rounded-lg">
          <p className="text-sm font-bold text-gray-900 dark:text-white">{data.stats.total_sessions}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Workouts</p>
        </div>
        <div className="flex-1 bg-gray-50 dark:bg-gray-800 p-2 rounded-lg">
          <p className="text-sm font-bold text-gray-900 dark:text-white">{data.stats.total_sets}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Sets</p>
        </div>
        <div className="flex-1 bg-gray-50 dark:bg-gray-800 p-2 rounded-lg">
          <p className="text-sm font-bold text-gray-900 dark:text-white">{data.stats.total_favorites}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Favorites</p>
        </div>
        <div className="flex-1 bg-gray-50 dark:bg-gray-800 p-2 rounded-lg">
          <p className="text-sm font-bold text-gray-900 dark:text-white">{data.stats.personal_records?.length || 0}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">PRs</p>
        </div>
      </div>

      {/* Achievements Grid */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm text-gray-900 dark:text-white">Achievements</h3>
        <button onClick={() => setShowAll(!showAll)} className="text-xs text-[#FF6600] font-semibold" data-testid="toggle-all-achievements">
          {showAll ? 'Show Less' : `View All (${data.total_count})`}
        </button>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
        {displayed.map(a => {
          const IconComp = ICON_MAP[a.icon] || Star;
          return (
            <div key={a.id} className={`relative flex flex-col items-center p-2 rounded-xl text-center transition-all ${
              a.unlocked ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-gray-50 dark:bg-gray-900 dark:bg-gray-700/30 opacity-50'
            }`} data-testid={`achievement-${a.id}`} title={a.description}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                a.unlocked ? 'bg-[#FF6600] text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-400'
              }`}>
                {a.unlocked ? <IconComp size={18} /> : <Lock size={14} />}
              </div>
              <p className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">{a.name}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight mt-0.5">{a.description}</p>
            </div>
          );
        })}
      </div>

      {/* Personal Records */}
      {data.stats.personal_records && data.stats.personal_records.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-2">Personal Records</h3>
          <div className="flex flex-wrap gap-2">
            {data.stats.personal_records.map((pr, i) => (
              <div key={i} className="bg-yellow-50 dark:bg-yellow-900/20 px-3 py-1.5 rounded-lg flex items-center gap-1.5" data-testid={`pr-${i}`}>
                <Trophy size={12} className="text-yellow-600" />
                <span className="text-xs font-semibold text-gray-900 dark:text-white">{pr.exercise_name}</span>
                <span className="text-xs text-[#FF6600] font-bold">{formatWeight(pr.weight, unit)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StreaksWidget;
